<template>
  <div
    :class="[
      'page-list',
      'page-list--company-contract-template'
    ]"
  >

    <ui-wad class="d-flex">
      <ui-cloak
        :is-visible="fetchState.status.value === 'success'"
      >
        <template #cloak>
          <default-placeholder
            class="page-list__default-list"
          />
        </template>
        <template #default>
          <default-list
            v-bind="{
              typeProfessionList,
              defaultCollection,
              contractListPerCodeTypeProfession,
              asDefaultCallback,
              resetDefaultCallback,
            }"
            :class="[
              'page-list__default-list',
              isPending ? 'page-list__default-list--busy' : ''
            ]"
          />
        </template>
      </ui-cloak>

      <transition
        v-if="
             hasACL(ACLS.HAMAC_COURRIER_DATA_GET)
          && ( fetchState.status.value === 'success' )
        "
        appear
        appear-active-class="animate__animated animate__fadeIn a5 d3"
      >
        <ui-caveat
          class="page-list__admin-caveat"
          body-class="pt-0 pl-0 pr-0"
        >
          <div class="page-list__admin-caveat__admin-header">
            <div class="page-list__admin-caveat__admin-header__text">
              Autres fonctionnalités pour toutes les sociétés
            </div>
            <layout-breadcrumb-structure
              :size="UI.SIZE.SMALL"
              :structure="fromCompanyLevel().structure"
            />
          </div>

          <ui-link
            class="page-list__admin-caveat__admin-item"
            :route="adminListRoute"
          >
            <ui-icon name="feed" />
            Modèles de contrats
          </ui-link>

          <ui-link
            :class="[
              'page-list__admin-caveat__admin-item',
              'page-list__admin-caveat__admin-item--sub'
            ]"
            :route="adminCreationRoute"
          >
            <ui-icon name="add" />
            Ajouter un modèle
          </ui-link>

        </ui-caveat>
      </transition>

    </ui-wad>

    <ui-cloak
      :is-visible="fetchState.status.value === 'success'"
    >
      <template #cloak>
        <main-placeholder
          class="page-list__contract-list"
        />
      </template>
      <template #default>
        <main-list
          v-bind="{
            typeProfessionList,
            deleteCallback,
            duplicateCallback,
            asDefaultCallback,
            defaultCollection,
            listCallback,
          }"
          :class="[
            'page-list__contract-list',
            isPending ? 'page-list__contract-list--busy' : ''
          ]"
        />
      </template>
    </ui-cloak>

  </div>

  <nuxt-page />

</template>


<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/hamac/variables'

.page-list
  $self: &

  padding-top: $layout-spacer-y
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y

  //////////////////////////////////

  &__default-list
    width: $contract-template-default-list-width
    margin-bottom: $layout-spacer-y

  &__contract-list--busy,
  &__default-list--busy
    @include no-interaction
    opacity: .6
    transition: opacity 0.3s
    transition-delay: opacity 0.3s

  //////////////////////////////////

  &__admin-caveat
    background-color: $contract-template-structure-body
    margin-bottom: $layout-spacer-y
    margin-left: $layout-spacer-x
    align-self: flex-start

    &__admin-header
      padding-top: $card-spacer-y * .75
      padding-bottom: $card-spacer-y * .75
      padding-left: $card-spacer-x
      padding-right: $card-spacer-x * 3
      margin-bottom: $card-spacer-y
      border-top-left-radius: $card-border-radius
      border-top-right-radius: $card-border-radius
      background: $contract-template-structure-header

      &__text
        color: $wine-berry
        font-weight: $font-weight-light

      :deep(.breadcrumb-structure__content)
        color: $wine-berry


    &__admin-item
      color: $primary
      font-size: 14.5px
      padding-top: space-x(.75)
      padding-bottom: space-x(.75)
      padding-left: $card-spacer-x
      padding-right: $card-spacer-x
      font-family: $font-family-heading
      font-weight: $font-weight-bold
      position: relative
      display: block

      .ui-icon
        margin-right: space-x(1.5)
        font-size: 18px
        vertical-align: sub

      &--sub
        font-size: 13.25px

      .ui-icon
        margin-right: space-x(1.5)
        font-size: 18px

      &::before
        @include virtual-content
        position: absolute
        width: 3px
        top: 0
        bottom: 0
        left: 0

      &:hover,
      &:focus,
      &:active
        background: $contract-template-structure-item-hover
        color: $wine-berry

        &::before
          background-color: $wine-berry

</style>

<script lang="ts">
import DefaultList from '~/components/hamac/contract-template/list/company/DefaultList.vue'
import DefaultPlaceholder from '~/components/hamac/contract-template/list/DefaultPlaceholder.vue'
import MainList from '~/components/hamac/contract-template/list/company/MainList.vue'
import MainPlaceholder from '~/components/hamac/contract-template/list/MainPlaceholder.vue'
import Console from '~/utils/lib/core/console'
import { clone, noop, pipe_, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getDetailsRoute } from '~/stores/hamac/contract-template/company/index'
import { getCreationRoute as getAdminCreationRoute, getListRoute as getAdminListRoute } from '~/stores/hamac/contract-template/structure/index'
import { groupByCodeTypeProfession, type ContractTemplate } from '~/stores/hamac/contract-template'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useStore_hamac_contractTemplate_company_list } from '~/stores/hamac/contract-template/company/list'
import { useStore_hamac_contractTemplate_company_details } from '~/stores/hamac/contract-template/company/details'
import { useStore_hamac_profession_typeProfession, type TypeProfessionHasDefaultContractTemplate } from '~/stores/hamac/profession/type-profession'
import type { ListInputSchema } from '~/utils/infra/common/types_'
import type { TypeProfession } from '~/stores/hamac/profession'

const console = new Console('component:contract-list')

export default defineNuxtComponent({
  components: { DefaultList, MainList, DefaultPlaceholder, MainPlaceholder },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Modèles de contrat — ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const store_contractTemplate_company_list = useStore_hamac_contractTemplate_company_list()
    const store_contractTemplate_company_details = useStore_hamac_contractTemplate_company_details()
    const store_typeProfession = useStore_hamac_profession_typeProfession()

    return {
      ...navigationContext,
      ...useAccessControl(),
      ...useLinkHandler(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      store_contractTemplate_company_list,
      store_contractTemplate_company_details,
      store_typeProfession,
    }
  },
  data() {
    const {
      structure: { id },
      company: { code_societe }
      // @ts-expect-error: "data" hook not injecting "setup" dependencies
    } = this.fromCompanyLevel()

    const adminCreationRoute = getAdminCreationRoute(id, code_societe)
    const adminListRoute = getAdminListRoute(id, code_societe)

    return {
      adminCreationRoute,
      adminListRoute,
      contractListPerCodeTypeProfession: {} as Record<TypeProfession["code"], ContractTemplate[]>, // (lazy)
      typeProfessionList: [] as TypeProfession[], // (lazy)
      defaultCollection: {} as TypeProfessionHasDefaultContractTemplate, // (lazy)
      isPending: false,
    }
  },
  created() {
    this.fetchWrapper(
      () => this.refreshAll()
    )
  },
  methods: {
    listCallback(input) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        input,
      }

      return this.store_contractTemplate_company_list.read(options)
        .catch(tapCatch(err => {
          console.warn('listCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de modèles de contrat`)
            .show()
        }))
    },
    deleteCallback({ id } :ContractTemplate) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        id
      }

      return this.store_contractTemplate_company_details.delete(options)
      .then(() => {
          // detached synchronisation
          this.refreshAll()
        })
        .catch(tapCatch(err => {
          console.warn('deleteCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Suppression d'un modèle de contrat`)
            .show()
        }))
    },
    duplicateCallback(item) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        item,
      }

      return this.store_contractTemplate_company_details.duplicate(options)
        .then(({ id }) => {
          const {
            structure: { id: structure_id },
            company: { code_societe }
          } = this.fromCompanyLevel()

          const url = getDetailsRoute(structure_id, code_societe, id)

          this.goToRoute(url)
            .then(
              () => this.ToastBuilder
                .success()
                .class('duplicate-toast')
                .title(`Modèle de contrat dupliqué`)
                .show()
            )
            .catch(noop)
        })
        .catch(err => {
          console.warn('duplicateCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .class('duplicate-toast')
            .title(`Duplication de modèle`)
            .show()
        })
    },
    asDefaultCallback(item, code_type_profession) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        item,
        code_type_profession
      }

      return this.store_contractTemplate_company_details.asDefault(options)
        .then(() => {
          // detached synchronisation
          this.refreshAll()
        })
        .catch(tapCatch(err => {
          console.warn('asDefaultCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Modèle par défaut`)
            .show()
        }))
    },
    resetDefaultCallback(code_type_profession) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        code_type_profession
      }

      return this.store_contractTemplate_company_list.resetDefault(options)
        .then(() => {
          // detached synchronisation
          this.refreshAll()
        })
        .catch(tapCatch(err => {
          console.warn('resetDefaultCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Modèle par défaut`)
            .show()
        }))
    },
    refreshAll() {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      const contractTemplateInput = {
        per_page: 10000, // eager
        order: 'updated_at',
        sort: 'desc',
      } satisfies ListInputSchema

      this.isPending = true

      return Promise.all([
          this.store_contractTemplate_company_list.read(
            { ...options, input: contractTemplateInput }
          ),
          this.store_typeProfession.read_(
            options
          ),
          this.store_contractTemplate_company_list.defaultCollection(
            options
          ),
        ])
        .then(results => {
          this.contractListPerCodeTypeProfession = pipe_(
            results[0].data,
            groupByCodeTypeProfession
          )

          this.typeProfessionList = clone(results[1])

          this.defaultCollection = clone(results[2])
        })
        .catch(tapCatch(err => {
          console.warn('fetch', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Liste modèles de contrat`)
            .show()
        }))
        .finally(() => this.isPending = false)
    },
  }
})
</script>
